/**
 * Custom hook for persisting filter state in URL search params
 * This allows filters to be maintained when navigating back to a page.
 *
 * When a `storageKey` is provided, filters are also saved to sessionStorage.
 * If the user navigates away and returns to the page (with no URL search params),
 * the previously saved filters are automatically restored.
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';

interface UseUrlFiltersOptions {
  /** Default values for filters when not present in URL */
  defaults?: Record<string, string>;
  /** If set, persist filters to sessionStorage under this key so they survive navigation */
  storageKey?: string;
}

interface UseUrlFiltersReturn {
  /** Get a filter value from URL params, with fallback to default */
  getFilter: (key: string) => string;
  /** Set a single filter value */
  setFilter: (key: string, value: string) => void;
  /** Set multiple filter values at once */
  setFilters: (filters: Record<string, string>) => void;
  /** Clear all filters (reset to defaults) */
  clearFilters: () => void;
  /** Get all current filter values */
  getAllFilters: () => Record<string, string>;
}

export function useUrlFilters(options: UseUrlFiltersOptions = {}): UseUrlFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const { defaults = {}, storageKey } = options;
  const hasRestored = useRef(false);

  // On mount, restore filters from sessionStorage if URL has no search params
  useEffect(() => {
    if (!storageKey || hasRestored.current) return;
    hasRestored.current = true;

    if (searchParams.toString() === '') {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          setSearchParams(new URLSearchParams(saved), { replace: true });
        }
      } catch {
        // sessionStorage may be unavailable (e.g. private browsing)
      }
    }
  }, [storageKey, searchParams, setSearchParams]);

  // Persist current filters to sessionStorage whenever they change
  useEffect(() => {
    if (!storageKey) return;
    try {
      const paramsString = searchParams.toString();
      if (paramsString) {
        sessionStorage.setItem(storageKey, paramsString);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, [searchParams, storageKey]);

  const getFilter = useCallback((key: string): string => {
    return searchParams.get(key) || defaults[key] || '';
  }, [searchParams, defaults]);

  const setFilter = useCallback((key: string, value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value && value !== defaults[key]) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams, defaults]);

  const setFilters = useCallback((filters: Record<string, string>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== defaults[key]) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams, defaults]);

  const clearFilters = useCallback(() => {
    if (storageKey) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // sessionStorage may be unavailable
      }
    }
    setSearchParams({}, { replace: true });
  }, [setSearchParams, storageKey]);

  const getAllFilters = useCallback((): Record<string, string> => {
    const filters: Record<string, string> = { ...defaults };
    searchParams.forEach((value, key) => {
      filters[key] = value;
    });
    return filters;
  }, [searchParams, defaults]);

  return {
    getFilter,
    setFilter,
    setFilters,
    clearFilters,
    getAllFilters,
  };
}
